/**
 * Application state — holds all runtime data for the benchmark UI.
 */

import type {
  BenchmarkMode,
  ComparisonResult,
  ModeResult,
  PaneMode,
  ReplayMeta,
  RunInfo,
  ScenarioInfo,
  TimelineEvent,
} from "./types.js";
import { toPaneMode } from "./types.js";

export type AppPhase = "idle" | "running" | "done" | "replaying";

export type PaneState = {
  events: TimelineEvent[];
  phase: string;
  elapsed: number;
  result: ModeResult | null;
};

export type AppState = {
  phase: AppPhase;
  gatewayOnline: boolean;
  wsConnected: boolean;
  scenarios: ScenarioInfo[];
  selectedScenarioId: string;
  treatment: PaneState;
  baseline: PaneState;
  comparison: ComparisonResult | null;
  // Replay state
  replayMode: boolean;
  replaySpeed: number;
  replayPaused: boolean;
  replayProgress: number; // 0-1
  replayElapsed: number;
  replayTotal: number;
  replayDemoMode: boolean;
  replayMeta: ReplayMeta | null;
  availableRuns: RunInfo[];
  showRunPicker: boolean;
};

export function createInitialPaneState(): PaneState {
  return { events: [], phase: "", elapsed: 0, result: null };
}

function resolvePaneKey(mode: BenchmarkMode | PaneMode): PaneMode {
  return toPaneMode(mode as BenchmarkMode);
}

export function createInitialState(): AppState {
  return {
    phase: "idle",
    gatewayOnline: false,
    wsConnected: false,
    scenarios: [],
    selectedScenarioId: "",
    treatment: createInitialPaneState(),
    baseline: createInitialPaneState(),
    comparison: null,
    replayMode: false,
    replaySpeed: 4,
    replayPaused: false,
    replayProgress: 0,
    replayElapsed: 0,
    replayTotal: 0,
    replayDemoMode: true,
    replayMeta: null,
    availableRuns: [],
    showRunPicker: false,
  };
}

/** Simple observable state — subscribers are called on every change. */
export class StateStore {
  private state: AppState;
  private listeners: Array<(state: AppState) => void> = [];

  constructor() {
    this.state = createInitialState();
  }

  get(): AppState {
    return this.state;
  }

  update(partial: Partial<AppState>) {
    this.state = { ...this.state, ...partial };
    for (const fn of this.listeners) fn(this.state);
  }

  updatePane(mode: BenchmarkMode | PaneMode, partial: Partial<PaneState>) {
    const key = resolvePaneKey(mode);
    this.state = { ...this.state, [key]: { ...this.state[key], ...partial } };
    for (const fn of this.listeners) fn(this.state);
  }

  resetPane(mode: BenchmarkMode | PaneMode) {
    const key = resolvePaneKey(mode);
    this.state = { ...this.state, [key]: createInitialPaneState() };
    for (const fn of this.listeners) fn(this.state);
  }

  pushEvent(mode: BenchmarkMode | PaneMode, event: TimelineEvent) {
    const key = resolvePaneKey(mode);
    const pane = this.state[key];
    const nextEvents = [...pane.events];
    const existingIndex = nextEvents.findIndex((item) => item.id === event.id);
    if (existingIndex !== -1) {
      nextEvents.splice(existingIndex, 1);
    }
    const insertAt = nextEvents.findIndex((item) => item.elapsed > event.elapsed);
    if (insertAt === -1) {
      nextEvents.push(event);
    } else {
      nextEvents.splice(insertAt, 0, event);
    }
    this.state = {
      ...this.state,
      [key]: { ...pane, events: nextEvents },
    };
    for (const fn of this.listeners) fn(this.state);
  }

  reset() {
    const { scenarios, selectedScenarioId, gatewayOnline, wsConnected, availableRuns } = this.state;
    this.state = {
      ...createInitialState(),
      scenarios,
      selectedScenarioId,
      gatewayOnline,
      wsConnected,
      availableRuns,
    };
    for (const fn of this.listeners) fn(this.state);
  }

  subscribe(fn: (state: AppState) => void): () => void {
    this.listeners.push(fn);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== fn);
    };
  }
}
